import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, TrendingUp, TrendingDown, Brain, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SessionFeedback({ sessionId, scores, timeElapsed, onClose }) {
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  const totalCorrect = scores.reduce((s, sc) => s + (sc.correct || 0), 0);
  const totalItems = scores.reduce((s, sc) => s + (sc.total || 0), 0);
  const avgAccuracy = scores.length > 0
    ? Math.round(scores.reduce((s, sc) => s + (sc.accuracy || 0), 0) / scores.length)
    : 0;

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const [fbRes, recRes] = await Promise.all([
          axios.get(`${API}/adaptive/feedback/${sessionId}`, { withCredentials: true }),
          axios.get(`${API}/adaptive/recommend`, { withCredentials: true })
        ]);
        setFeedback(fbRes.data);
        setRecommendations(recRes.data.recommendations?.slice(0, 3) || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchFeedback();
  }, [sessionId]);

  const minutes = Math.floor(timeElapsed / 60);

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-y-auto" data-testid="session-feedback">
      <div className="min-h-screen flex flex-col items-center justify-center p-4 md:p-12">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <div className="text-center mb-10 animate-fade-in">
            <Brain className="w-10 h-10 text-[#09090B] mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="feedback-title">
              Session Complete
            </h1>
            <p className="mono-data text-sm text-zinc-500">{minutes} minutes of focused training</p>
          </div>

          {/* Score Grid */}
          <div className="grid grid-cols-3 gap-0 border border-[#09090B] mb-8 animate-slide-up stagger-1" style={{ animationFillMode: 'backwards' }}>
            <div className="p-6 border-r border-[#09090B] text-center">
              <div className="label-swiss mb-1">Items Recalled</div>
              <div className="text-3xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="feedback-items">
                {totalCorrect}/{totalItems}
              </div>
            </div>
            <div className="p-6 border-r border-[#09090B] text-center">
              <div className="label-swiss mb-1">Accuracy</div>
              <div className="text-3xl font-black text-[#09090B]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="feedback-accuracy">
                {avgAccuracy}%
              </div>
            </div>
            <div className="p-6 text-center">
              <div className="label-swiss mb-1">Rating</div>
              <div className="text-lg font-bold" data-testid="feedback-rating">
                {avgAccuracy >= 80 ? 'Excellent' : avgAccuracy >= 60 ? 'Good' : 'Keep Going'}
              </div>
            </div>
          </div>

          {/* AI Feedback */}
          <div className="border border-[#09090B] p-6 mb-8 animate-slide-up stagger-2" style={{ animationFillMode: 'backwards' }} data-testid="ai-feedback-box">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-[#09090B]" />
              <span className="mono-data text-xs text-[#09090B]">
                {feedback?.source === 'ai' ? 'AI Coach Feedback' : 'Performance Feedback'}
              </span>
            </div>
            {loading ? (
              <div className="mono-data text-sm animate-pulse-red">Generating feedback...</div>
            ) : (
              <p className="text-zinc-700 leading-relaxed" data-testid="feedback-text">
                {feedback?.feedback || 'Great effort! Keep training to see your improvement.'}
              </p>
            )}

            {/* Trend indicator */}
            {feedback && feedback.trend !== undefined && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-zinc-200">
                {feedback.trend >= 0 ? (
                  <TrendingUp className="w-4 h-4 text-[#09090B]" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-[#09090B]" />
                )}
                <span className={`mono-data text-xs ${feedback.trend >= 0 ? 'text-[#09090B]' : 'text-[#09090B]'}`}>
                  {feedback.trend > 0 ? '+' : ''}{feedback.trend}% trend
                </span>
                {feedback.weak_areas?.length > 0 && (
                  <span className="mono-data text-xs text-zinc-400 ml-4">
                    Focus: {feedback.weak_areas.join(', ')}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <div className="mb-8 animate-slide-up stagger-3" style={{ animationFillMode: 'backwards' }}>
              <h3 className="mono-data text-xs text-zinc-500 mb-3">Recommended Next</h3>
              <div className="space-y-2">
                {recommendations.slice(0, 2).map((rec, i) => (
                  <Link
                    key={i}
                    to={rec.session_id ? `/session/${rec.session_id}` : '/programs'}
                    className="border border-[#09090B] p-4 flex items-center justify-between hover:bg-zinc-50 transition-colors"
                    data-testid={`fb-rec-${i}`}
                  >
                    <div>
                      <span className={`inline-block w-2 h-2 mr-2 ${rec.priority === 'high' ? 'bg-[#09090B]' : 'bg-[#09090B]'}`} />
                      <span className="text-sm font-medium">{rec.title}</span>
                      <span className="text-xs text-zinc-500 ml-2">{rec.message}</span>
                    </div>
                    <ArrowRight className="w-4 h-4 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-center gap-4 animate-slide-up stagger-4" style={{ animationFillMode: 'backwards' }}>
            <Link to="/profile" className="btn-secondary flex items-center gap-2" data-testid="view-profile-btn">
              View Cognitive Profile
            </Link>
            <Link to="/dashboard" className="btn-primary flex items-center gap-2" data-testid="back-to-dashboard-btn">
              Dashboard <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
